/**
 * 手动赠送课时弹窗（ADM-02 GRANT，manual-trial-grant-admin-ui:SC-02~SC-08）。
 *
 * 无报名单据的试课入账入口：在售 SKU 下拉（选中默认带出 trialSessionCount，
 * 无配置默认 1，可手动改 1~3）、原因必填 ≤255（trim 校验）、所选 SKU 试课
 * 余额提示。幂等键弹窗打开时生成 UUID、失败重试复用（useRef 不随提交重置）、
 * 成功关闭后重开为新 UUID；409 业务冲突按信封错误码映射中文提示且保留
 * 已填内容。提交产生动作级 span：action: entitlement.manualGrant
 * （属性白名单仅 studentId/skuId/idempotencyKey 三个 UUID）。
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { Alert, Button, Form, Input, InputNumber, Select, Space } from 'antd';
import type { FormInstance } from 'antd';
import { message } from '../../utils/antd-message';
import { GiftOutlined } from '@ant-design/icons';
import { DraggableModal } from '../../components/DraggableModal';
import { courseSkuApi, type CourseSkuItem } from '../../api/course-skus.api';
import { entitlementLedgerApi } from '../../api/entitlementLedger.api';
import {
  manualTrialGrantApi,
  type ManualTrialGrantResult,
} from '../../api/manual-trial-grant.api';
import { ApiError } from '../../api/transport';
import { startAction } from '../../observability/otel';

/** 409 业务冲突信封错误码 → 中文提示（SC-06；未登记码回退服务端文案）。已购学生赠送已放开（H- 系 hotfix），仅作废账户仍拦截。 */
const CONFLICT_MESSAGE_BY_CODE: Record<string, string> = {
  'entitlement.manual_trial_grant_account_voided': '该学员此课程权益账户已作废，不可赠送课时',
  'entitlement.manual_trial_grant_student_archived': '学生已归档，不可赠送课时',
  'entitlement.manual_trial_grant_sku_not_on_shelf': '课程 SKU 非在售，不可赠送课时',
};

interface GrantFormValues {
  skuId: string | undefined;
  quantity: number | null;
  reason: string;
}

export function ManualTrialGrantModal({
  studentId,
  studentName,
  studentNo,
  open,
  onClose,
  onGranted,
}: {
  studentId: string;
  studentName: string;
  studentNo: string;
  open: boolean;
  onClose: () => void;
  /** 赠送成功回调（父级刷新账本 Tab 等）。 */
  onGranted: () => void;
}) {
  const [form] = Form.useForm<GrantFormValues>();
  const [skus, setSkus] = useState<CourseSkuItem[]>([]);
  const [skusLoading, setSkusLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [conflictMessage, setConflictMessage] = useState<string | null>(null);
  /** 幂等键：打开时生成，失败重试复用（不重置），关闭后由下次打开覆盖。 */
  const idempotencyKeyRef = useRef('');

  // 打开时加载在售 SKU 并生成新幂等键（SC-07：成功后重开为新 UUID）
  useEffect(() => {
    if (!open) return;
    idempotencyKeyRef.current = crypto.randomUUID();
    setConflictMessage(null);
    form.resetFields();
    setSkusLoading(true);
    void courseSkuApi
      .listCourseSkus({ status: 'ON_SHELF', page: 1, pageSize: 100 })
      .then((page) => setSkus(page.items))
      .catch(() => {
        message.error('在售课程加载失败，请稍后重试');
        setSkus([]);
      })
      .finally(() => setSkusLoading(false));
  }, [open, form]);

  async function handleSubmit(): Promise<void> {
    const values = await form.validateFields().catch(() => null);
    if (!values || !values.skuId) return;
    setSubmitting(true);
    setConflictMessage(null);
    // SC-08：属性白名单仅三个 UUID，禁止携带姓名/原因等
    const span = startAction('action: entitlement.manualGrant', {
      studentId,
      skuId: values.skuId,
      idempotencyKey: idempotencyKeyRef.current,
    });
    try {
      const result: ManualTrialGrantResult =
        await manualTrialGrantApi.createManualTrialGrant({
          studentId,
          skuId: values.skuId,
          quantity: values.quantity ?? 1,
          reason: values.reason,
          idempotencyKey: idempotencyKeyRef.current,
        });
      message.success(
        result.applied
          ? `赠送成功，当前赠送课余额 ${result.giftedAvailableQuantity} 次`
          : `本次请求为重复提交，当前赠送课余额 ${result.giftedAvailableQuantity} 次`,
      );
      span.end();
      onGranted();
      onClose();
    } catch (err) {
      span.end();
      if (err instanceof ApiError && err.status === 409) {
        setConflictMessage(CONFLICT_MESSAGE_BY_CODE[err.code] ?? err.message);
      } else {
        message.error(err instanceof Error ? err.message : '提交失败，请稍后重试');
      }
      // 失败保留已填内容与幂等键（SC-06/SC-07），不 resetFields
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <DraggableModal
      title={
        <Space>
          <GiftOutlined />
          赠送课时
        </Space>
      }
      open={open}
      onCancel={onClose}
      width={520}
      destroyOnHidden
      footer={
        <>
          <Button onClick={onClose} disabled={submitting}>
            取消
          </Button>
          <Button
            type="primary"
            loading={submitting}
            onClick={() => void handleSubmit()}
            data-testid="btn-submit-manual-trial-grant"
          >
            确认赠送
          </Button>
        </>
      }
    >
      <p style={{ marginBottom: 12 }} data-testid="grant-student-summary">
        学生：<strong>{studentName}</strong>（学号 {studentNo}）
      </p>
      {conflictMessage && (
        <Alert
          type="error"
          showIcon
          message={conflictMessage}
          style={{ marginBottom: 12 }}
          data-testid="grant-conflict-alert"
        />
      )}
      <Form
        form={form}
        layout="vertical"
        initialValues={{ quantity: 1 }}
        disabled={submitting}
      >
        <Form.Item
          name="skuId"
          label="课程 SKU（仅在售）"
          rules={[{ required: true, message: '请选择课程 SKU' }]}
        >
          <Select
            placeholder="请选择在售课程"
            loading={skusLoading}
            showSearch
            optionFilterProp="label"
            options={skus.map((s) => ({
              value: s.id,
              label: `${s.name}（${s.code}）`,
            }))}
            data-testid="select-grant-sku"
          />
        </Form.Item>
        <Form.Item
          name="quantity"
          label="赠送次数"
          rules={[
            {
              validator: (_rule, value: number | null) => {
                if (value === null || value === undefined) {
                  return Promise.reject(new Error('请填写赠送次数（1~3 的整数）'));
                }
                if (!Number.isInteger(value) || value < 1 || value > 3) {
                  return Promise.reject(new Error('赠送次数需为 1~3 的整数'));
                }
                return Promise.resolve();
              },
            },
          ]}
        >
          {/* 不设 precision：小数经 Form validator 统一拦截（0/4/小数/清空同路径提示） */}
          <InputNumber min={1} max={3} style={{ width: 120 }} />
        </Form.Item>
        <Form.Item
          name="reason"
          label="赠送原因"
          rules={[
            { required: true, whitespace: true, message: '请填写赠送原因' },
            { max: 255, message: '原因不超过 255 字符' },
          ]}
        >
          <Input.TextArea
            rows={3}
            maxLength={255}
            placeholder="如：老学员回馈、活动赠课等（必填）"
            data-testid="input-grant-reason"
          />
        </Form.Item>
      </Form>
      <SkuBalanceHint open={open} studentId={studentId} form={form} skus={skus} />
    </DraggableModal>
  );
}

/** 赠送余额提示（内层组件）：useWatch/余额查询/默认数量带出全量内聚，仅开窗挂载
 *  （Modal 带 destroyOnHidden），消除关窗态「useForm not connected」开发告警。 */
function SkuBalanceHint({
  open,
  studentId,
  form,
  skus,
}: {
  open: boolean;
  studentId: string;
  form: FormInstance<GrantFormValues>;
  skus: CourseSkuItem[];
}) {
  const selectedSkuId = Form.useWatch('skuId', form) as string | undefined;
  const selectedSku = useMemo(
    () => skus.find((s) => s.id === selectedSkuId) ?? null,
    [skus, selectedSkuId],
  );
  const [giftedBalance, setGiftedBalance] = useState<number | null>(null);
  const [balanceLoading, setBalanceLoading] = useState(false);

  // 选中 SKU：默认带出试课次数（无配置默认 1）+ 刷新赠送池余额提示（SC-02）
  useEffect(() => {
    if (!open || !selectedSku) return;
    form.setFieldValue('quantity', selectedSku.trialSessionCount || 1);
    let cancelled = false;
    setBalanceLoading(true);
    void entitlementLedgerApi
      .getStudentAccounts(studentId)
      .then((accounts) => {
        if (cancelled) return;
        // 单行双池：同一学生同一 SKU 唯一行，直接读赠送池
        const account = accounts.find((a) => a.skuId === selectedSku.id) ?? null;
        setGiftedBalance(account ? account.giftedAvailable : null);
      })
      .catch(() => {
        if (!cancelled) setGiftedBalance(null);
      })
      .finally(() => {
        if (!cancelled) setBalanceLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [open, selectedSku, studentId, form]);

  // 关窗复位余额提示，避免下次打开残留上次查询结果
  useEffect(() => {
    if (!open) setGiftedBalance(null);
  }, [open]);

  return (
    <p style={{ color: '#666', marginBottom: 0 }} data-testid="grant-balance-hint">
      {selectedSku
        ? balanceLoading
          ? '正在查询赠送课余额…'
          : giftedBalance === null
            ? `「${selectedSku.name}」尚无课时账户，首次赠送将自动创建`
            : `「${selectedSku.name}」当前赠送课余额：${giftedBalance} 次`
        : '选中课程后展示该学生当前赠送课余额'}
    </p>
  );
}
